import modelExtend from 'dva-model-extend';

import { getComments, getCommentsInStream, addComment, updateComment, deleteComment } from '../services/comment';
import { model } from './common';

function transform(map, ids) {
  ids.forEach((id) => {
    map[id] = { ...(map[id] || {}), replies: [] };
  });

  const topIds = [];
  ids.forEach((id) => {
    const child = map[id];
    if (map[child.replyTo]) {
      map[child.replyTo].replies.push(child);
    } else {
      topIds.push(id);
    }
  });
  return topIds;
}

function getTopParent(childId, store) {
  let node = store[childId];
  while (node) {
    const parent = node.replyTo && store[node.replyTo];
    if (!parent) break;
    node = parent;
  }
  return node;
}


export default modelExtend(model, {
  namespace: 'comment',

  state: {
    list: [],
    latest: [],
    starred: [],
    stream: {},
    store: {},

    comments: null,
    hasMoreComments: true,
    commentParams: {
      limit: 10,
      offset: 0,
    },
    highlightCommentId: null,
  },

  subscriptions: {
    setup({ dispatch, history }) {
      history.listen((location) => {
        if (location.pathname === '/comment') {
          const { commentId, streamId, ...rest } = location.query || {};
          dispatch({
            type: 'getComments',
            payload: rest,
          });
          dispatch({
            type: 'getLatest',
          });
          dispatch({
            type: 'getStarred',
          });
          dispatch({
            type: 'updateState',
            payload: { highlightCommentId: null },
          });
          if (commentId && streamId) {
            dispatch({
              type: 'getCommentsInStream',
              payload: {
                streamId,
                highlight: parseInt(commentId),
              },
            });
          }
        }
      });
    },
  },

  effects: {
    * getLatest({ output }, { call, put }) {
      const data = yield call(getComments, { notme: 1, subscribed: 1, newest: 1 });
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'getLatestSuccess',
          payload: {
            comments: data.comments,
          },
        });
      } else {
        throw data;
      }
    },
    * getStarred({ output }, { call, put }) {
      const data = yield call(getComments, { starred: 1 });
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'getStarredSuccess',
          payload: {
            comments: data.comments,
          },
        });
      } else {
        throw data;
      }
    },
    * getComments({ payload, output }, { call, put }) {
      const params = { offset: 0, limit: 10, root: 1, ...payload };
      const data = yield call(getComments, params);
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'getCommentsSuccess',
          payload: {
            comments: data.comments,
            params,
          },
        });
      } else {
        throw data;
      }
    },

    * getCommentsInStream({ payload, output }, { call, put }) {
      const { highlight, ...rest } = payload;
      const data = yield call(getCommentsInStream, rest);
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'getCommentsInStreamSuccess',
          payload: {
            comments: data.comments,
            params: payload,
          },
        });
      } else {
        throw data;
      }
    },

    * deleteComment({ payload, output }, { call, put }) {
      const data = yield call(deleteComment, payload);
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'deleteCommentSuccess',
          payload,
        });
      } else {
        throw data;
      }
    },

    * updateComment({ payload, output }, { call, put }) {
      const data = yield call(updateComment, payload);
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'updateCommentSuccess',
          payload: { params: payload, updated: data.updated },
        });
      } else {
        throw data;
      }
    },

    * addComment({ payload, output }, { call, put }) {
      const data = yield call(addComment, payload);
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'addCommentSuccess',
          payload: { params: payload, created: data.created },
        });
      } else {
        throw data;
      }
    },

    * loadMoreComments(_, { select, put }) {
      const { commentParams } = yield (select(({ comment }) => comment));
      commentParams.offset += commentParams.limit;
      yield put({ type: 'getComments', payload: { ...commentParams } });
    },

  },

  reducers: {
    getLatestSuccess(state, { payload: { comments } }) {
      const store = { ...state.store };
      const latest = [];
      comments.forEach((item) => {
        if (store[item.id]) {
          Object.assign(store[item.id], item);
        } else {
          store[item.id] = item;
        }
        latest.push(item.id);
      });
      return {
        ...state,
        store,
        latest,
      };
    },
    getStarredSuccess(state, { payload: { comments } }) {
      const store = { ...state.store };
      const starred = [];
      comments.forEach((item) => {
        if (store[item.id]) {
          Object.assign(store[item.id], item);
        } else {
          store[item.id] = item;
        }
        starred.push(item.id);
      });
      return {
        ...state,
        store,
        starred,
      };
    },
    getCommentsSuccess(state, { payload: { comments, params } }) {
      const store = { ...state.store };
      let list = null;
      if (params.offset) {
        list = [...state.list];
      } else {
        list = [];
      }
      comments.forEach((item) => {
        if (store[item.id]) {
          Object.assign(store[item.id], item);
        } else {
          store[item.id] = item;
        }
        list.push(item.id);
      });

      const highlightCommentId = state.highlightCommentId;
      if (highlightCommentId) {
        // Move highlight comment to top of list
        const parent = getTopParent(highlightCommentId, store);
        if (parent) {
          list = list.filter(id => id !== parent.id);
          list = [parent.id, ...list];
        }
      }
      return {
        ...state,
        store,
        list,
        commentParams: params,
        hasMoreComments: comments.length >= params.limit,
        highlightCommentId,
      };
    },

    getCommentsInStreamSuccess(state, { payload: { comments, params: { streamId, highlight } } }) {
      const store = { ...state.store };

      const ids = [];
      comments.forEach((item) => {
        store[item.id] = { ...(store[item.id] || {}), ...item };
        ids.push(item.id);
      });

      const topIds = transform(store, ids);

      const stream = { ...state.stream, [streamId]: { ids, topIds } };
      let list = state.list;
      let highlightCommentId = state.highlightCommentId;
      if (highlight) {
        // Move highlight comment to top of list
        const parent = getTopParent(highlight, store);
        if (parent) {
          list = list.filter(id => id !== parent.id);
          list = [parent.id, ...list];
          highlightCommentId = highlight;
        }
      }

      return {
        ...state,
        store,
        stream,
        list,
        highlightCommentId,
      };
    },

    deleteCommentSuccess(state, { payload }) {
      const { id, streamId } = payload;

      const list = state.list.filter(cId => cId !== id);
      const starred = state.starred.filter(cId => cId !== id);
      const latest = state.latest.filter(cId => cId !== id);

      const stream = { ...state.stream };
      const store = { ...state.store };

      if (stream[streamId]) {
        const ids = stream[streamId].ids.filter(cId => cId !== id);
        stream[streamId] = { ids, topIds: transform(store, ids) };
      }

      return { ...state, store, stream, list, starred, latest };
    },

    updateCommentSuccess(state, { payload: { params, updated } }) {
      const { id, streamId } = updated;
      const store = { ...state.store };
      store[id] = { ...(store[id] || {}), ...updated };
      if (state.stream[streamId]) {
        transform(store, state.stream[streamId].ids);
      }

      // check update starred
      let starred = state.starred;
      if ('starred' in params) {
        if (updated.starred && starred.indexOf(id) === -1) {
          starred = [...starred, id];
        } else {
          starred = starred.filter(o => o !== id);
        }
      }

      return { ...state, store, starred };
    },

    addCommentSuccess(state, { payload: { params, created } }) {
      created.replies = [];

      const { id, streamId } = created;
      const store = { ...state.store, [id]: created };

      const stream = { ...state.stream };
      const ids = stream[streamId] ? [...stream[streamId].ids, id] : [id];
      stream[streamId] = { ids, topIds: transform(store, ids) };

      let list = state.list;
      if (params.refType === 'space') {
        list = [id, ...state.list];
      }

      return { ...state, store, stream, list };
    },
  },
});



// WEBPACK FOOTER //
// ./src/models/comment.js